import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Tooltip,
  keyframes,
  SelectProps,
} from '@mui/material';
import { Warning, Check, Edit } from '@mui/icons-material';
import { useFormData } from '../../contexts/FormDataContext';
import { useConfidenceToggle } from '../../contexts/ConfidenceToggleContext';
import { useRef, useEffect, useState } from 'react';

// Glow animation — same as FormField
const glowPulse = keyframes`
  0% { box-shadow: 0 0 4px 1px rgba(0, 150, 136, 0.4); }
  50% { box-shadow: 0 0 10px 3px rgba(0, 150, 136, 0.6); }
  100% { box-shadow: 0 0 4px 1px rgba(0, 150, 136, 0.4); }
`;

interface SelectFieldOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  tabName: string;
  fieldName: string;
  label: string;
  options: SelectFieldOption[];
  defaultValue?: string;
  showConfidence?: boolean;
  confidenceThreshold?: number;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  /** Use to align border with section/form fields (e.g. borderColor: '#E0E0E0') */
  sx?: object;
}

export default function SelectField({
  tabName,
  fieldName,
  label,
  options,
  defaultValue = '',
  showConfidence,
  confidenceThreshold = 0.7,
  size = 'small',
  fullWidth = true,
  sx: sxProp,
}: SelectFieldProps) {
  const {
    getFieldValue,
    getFieldConfidence,
    getFieldSource,
    hasLowConfidence,
    updateField,
  } = useFormData();

  const { showConfidence: globalShowConfidence } = useConfidenceToggle();
  const effectiveShowConfidence = showConfidence !== undefined ? showConfidence : globalShowConfidence;

  const rawValue = getFieldValue(tabName, fieldName, defaultValue);
  const value = rawValue != null ? String(rawValue) : '';

  const confidence = getFieldConfidence(tabName, fieldName);
  const source = getFieldSource(tabName, fieldName);
  const lowConfidence = hasLowConfidence(tabName, fieldName, confidenceThreshold);

  // Glow effect — same logic as FormField
  const prevValueRef = useRef<string>(value);
  const [isGlowing, setIsGlowing] = useState(false);
  const glowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (
      value !== prevValueRef.current &&
      value !== '' &&
      source !== 'manual' &&
      source !== 'database'
    ) {
      setIsGlowing(true);
      if (glowTimerRef.current) clearTimeout(glowTimerRef.current);
      glowTimerRef.current = setTimeout(() => setIsGlowing(false), 8000);
    }
    prevValueRef.current = value;
  }, [value, source]);

  const handleChange = (event: any) => {
    updateField(tabName, fieldName, event.target.value);
  };

  const getConfidenceColor = () => {
    if (confidence >= 0.9) return '#009688';
    if (confidence >= 0.7) return '#ff9800';
    return '#e91e63';
  };

  const getSourceIcon = () => {
    switch (source) {
      case 'ambient':
        return <Check sx={{ fontSize: 14 }} />;
      case 'llm_correction':
        return <Warning sx={{ fontSize: 14 }} />;
      case 'manual':
        return <Edit sx={{ fontSize: 14 }} />;
      default:
        return undefined;
    }
  };

  const getSourceLabel = () => {
    switch (source) {
      case 'ambient':
        return 'Auto-filled from conversation';
      case 'llm_correction':
        return 'AI-corrected';
      case 'manual':
        return 'Manually entered';
      default:
        return 'Not filled';
    }
  };

  const showBadge = effectiveShowConfidence && source !== 'manual' && source !== 'database';

  return (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <FormControl
        size={size}
        fullWidth={fullWidth}
        sx={[
          {
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              '& fieldset': {
                borderColor: isGlowing
                  ? '#009688'
                  : lowConfidence
                    ? '#e91e63'
                    : '#8DBAEA',
                borderWidth: isGlowing || lowConfidence ? 2 : 1,
              },
              '&:hover fieldset': { borderColor: '#7aa8e0' },
              '&.Mui-focused fieldset': { borderColor: '#012676', borderWidth: 1 },
              ...(isGlowing && {
                animation: `${glowPulse} 1.5s ease-in-out infinite`,
                borderRadius: 1,
              }),
            },
          },
          ...(sxProp ? (Array.isArray(sxProp) ? sxProp : [sxProp]) : []),
        ]}
      >
        <InputLabel>{label}</InputLabel>
        <Select
          value={value}
          onChange={handleChange}
          label={label}
        >
          <MenuItem value="">
            <em>Select...</em>
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {showBadge && (
        <Tooltip title={`${getSourceLabel()} (${(confidence * 100).toFixed(0)}% confidence)`}>
          <Chip
            icon={getSourceIcon()}
            label={`${(confidence * 100).toFixed(0)}%`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              bgcolor: getConfidenceColor(),
              color: 'white',
              '& .MuiChip-icon': { color: 'white' },
              flexShrink: 0,
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
}
